// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseToolbar, Loading } from '~/components/common';
import { ActivityDetailModal, BaseTimeLine } from '~/components/module/activity';
// Others
import { LoadingSizeEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { IActivity } from '~/utils/interface/activity';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getOrderDetail } from '~/thunks/order/orderThunk';
import { IParamQueryOrderDetail } from '~/utils/interface/order';
import { IBaseResponse, IListDataResponse } from '~/utils/interface/base';
// Styles, images, icons
import styles from './CallHistoryOrderTab.module.scss';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const CallHistoryOrderTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activityList, setActivityList] = useState<IActivity[]>([]);
  const [currentPageActivity, setCurrentPageActivity] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMoreActivity, setHasMoreActivity] = useState<boolean>(true);
  const [activitySelected, setActivitySelected] = useState<IActivity | null>(null);
  const [isShowActivityDetail, setIsShowActivityDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook

  useEffect(() => {
    if (!orderId) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId,
      type: TypeDetailClientEnum.HISTORY,
      query: {
        page: currentPageActivity,
        limit: DEFAULT_LIMIT_PAGE,
      },
    };

    handleGetListActivityOrder(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageActivity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListActivityOrder = (params: IParamQueryOrderDetail) => {
    setIsLoading(currentPageActivity === DEFAULT_CURRENT_PAGE);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IActivity[]>>;

        if (!data) return;

        const { responses } = data;

        if (!responses || responses?.length === 0) {
          setHasMoreActivity(false);

          return;
        }

        setActivityList((prev) => {
          const newData = responses?.filter((data) => !prev?.some((oldActivity) => oldActivity?.id === data?.id));

          const updateList = [...prev, ...newData];

          setHasMoreActivity(true);

          return updateList;
        });
      })
      .catch((_error) => {
        setHasMoreActivity(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLoadMore = () => {
    setCurrentPageActivity((prev) => prev + 1);
  };

  const handleViewDetail = (activity: IActivity) => {
    setIsShowActivityDetail(true);
    setActivitySelected(activity);
  };

  const handleCloseActivityDetail = () => {
    setIsShowActivityDetail(false);
    setActivitySelected(null);
  };
  //#endregion Handle Function

  return (
    <Box id='orderDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('history_title')} description={t('history_description')} />
      </Stack>

      {!isLoading &&
        (activityList?.length > 0 ? (
          <BaseTimeLine
            data={activityList}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreActivity}
            onView={handleViewDetail}
            loadMoreSize={LoadingSizeEnum.MEDIUM}
          />
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        ))}

      <ActivityDetailModal
        isOpen={isShowActivityDetail}
        {...(activitySelected && { activity: activitySelected })}
        onClose={handleCloseActivityDetail}
      />

      {isLoading && <Loading />}
    </Box>
  );
};

export default CallHistoryOrderTab;
